import React from "react";
import { Dices } from "lucide-react";
import LpcAvatar from "./LpcAvatar";
import AgentAvatarControls from "./AgentAvatarControls";
import { PALETTES, SKIN_AGENT, SKIN_HUMAN, HAIR_COLORS } from "./palettes";
import { WARDROBE } from "./wardrobe";
import { resolveAvatar, shuffleSeed } from "./identity";

import type { AvatarLook } from "@/shared/types";
import type { Material } from "./palettes";

/* ----------------------------------------------------------------------------
   Avatar customizer — shuffle plus four dials.

   Colour is picked from swatches rather than a dropdown: the names in the LPC
   palettes ("taupe", "sandy", "raven") mean nothing until you see them, and a
   22-item select of words is a worse control than 22 dots.

   Skin options are filtered by `human`, so the agent/human split can't be
   customised away — an agent has no human skin tone to choose.
---------------------------------------------------------------------------- */

const swatch = (material: Material, ramp: string) => (PALETTES[material]?.[ramp] || [])[3] || "#888";

function Swatches({ options, material, value, onChange }: {
  options: string[];
  material: Material;
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((id) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          title={id.replace(/_/g, " ")}
          aria-label={id.replace(/_/g, " ")}
          aria-pressed={value === id}
          className="press w-6 h-6 rounded-full shrink-0"
          style={{
            background: swatch(material, id),
            boxShadow:
              value === id
                ? "0 0 0 2px var(--surface), 0 0 0 3.5px var(--text-primary)"
                : "inset 0 0 0 1px rgba(0,0,0,.25)",
          }}
        />
      ))}
    </div>
  );
}

function Chips({ options, value, onChange }: {
  options: Array<{ id: string; label: string }>;
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={`press px-2.5 h-7 rounded-[8px] text-[12.5px] whitespace-nowrap ${
            value === o.id
              ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
              : "text-[var(--text-tertiary)] ring-1 ring-[var(--hairline)] hover:text-[var(--text-primary)]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[12px] text-[var(--text-tertiary)] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

export interface AvatarCustomizerProps {
  seed: string;
  hue?: number;
  occupiedAvatars?: readonly { seed: string; hue: number }[];
  avatar: AvatarLook | null;
  human?: boolean;
  onChange: (next: { seed: string; hue?: number; avatar: AvatarLook | null }) => void;
}

export default function AvatarCustomizer({ seed, avatar, human = false, hue, occupiedAvatars, onChange }: AvatarCustomizerProps) {
  if (!human) return <AgentAvatarControls hue={hue} occupiedAvatars={occupiedAvatars} seed={seed} avatar={avatar} onChange={onChange} />;
  const resolved = resolveAvatar(seed, { human, override: avatar });
  const set = (key: keyof AvatarLook) => (val: string) => onChange({ seed, avatar: { ...resolved, [key]: val } });

  // Shuffle rerolls the seed and drops the overrides, so it produces a genuinely
  // new character rather than nudging one field of the current one.
  const shuffle = () => onChange({ seed: shuffleSeed(), avatar: null });

  return (
    <div className="flex flex-col sm:flex-row gap-5">
      <div className="flex flex-col items-center gap-2.5 shrink-0">
        <LpcAvatar seed={seed} avatar={resolved} human={human} size={72} ring animate="alive" />
        <button
          onClick={shuffle}
          className="press flex items-center gap-1.5 px-2.5 h-7 rounded-[8px] text-[12.5px] text-[var(--text-secondary)] ring-1 ring-[var(--hairline)] hover:text-[var(--text-primary)]"
        >
          <Dices className="w-3.5 h-3.5" strokeWidth={1.75} /> Shuffle
        </button>
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-3.5">
        <Field label="Head">
          <Chips
            options={WARDROBE.heads.map((h) => ({ id: h.id, label: h.name.replace("Human ", "") }))}
            value={resolved.head}
            onChange={set("head")}
          />
        </Field>
        <Field label="Hair">
          <Chips
            options={WARDROBE.hair.map((h) => ({ id: h.id, label: h.name }))}
            value={resolved.hair}
            onChange={set("hair")}
          />
        </Field>
        <Field label="Hair colour">
          <Swatches options={HAIR_COLORS} material="hair" value={resolved.hairColor} onChange={set("hairColor")} />
        </Field>
        <Field label={human ? "Skin" : "Chassis"}>
          <Swatches
            options={human ? SKIN_HUMAN : SKIN_AGENT}
            material="skin"
            value={resolved.skin}
            onChange={set("skin")}
          />
        </Field>
      </div>
    </div>
  );
}
